import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import StatusPill from '../ui/StatusPill'
import { KILO } from '../../constants/KILO_THEME'
import { getDayLabel } from '../../lib/days'
import { fetchSessionDebrief, type DebriefActionId, type SessionDebrief } from '../../lib/sessionDebrief'
import { sanitizeCoachText } from '../../lib/aiResponseValidator'
import CollapsedDisclaimer from '../workout/CollapsedDisclaimer'
import { upcomingTrainingBlocks } from '../../lib/trainingSchedule'
import type { GroceryItem, MealBlock, TrainingBlock } from '../../lib/protocolTypes'
import { saveWorkoutFeedback, type InjuryStatus } from '../../lib/workoutSession'

type PostWorkoutSummaryProps = {
  userId?: string | null
  scheduleId: string
  setProgress: number
  block: TrainingBlock
  training: TrainingBlock[]
  dayNumber: number
  hasWearableConnected: boolean
  calendarSyncEnabled: boolean
  dietaryLabel?: string
  meals?: MealBlock[]
  grocery?: GroceryItem[]
  onDismiss: () => void
  onOpenShopping?: () => void
  onOpenMeals?: () => void
  onConnectCalendar?: () => void
}

const INJURY_OPTIONS: InjuryStatus[] = ['NONE', 'DISCOMFORT', 'ACUTE']

const RPE_SLIDER_CLASS =
  'kilo-range mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-neutral-200 accent-kilo-accent dark:bg-kilo-nested'

function InjuryFeelControls({
  sessionRpe,
  injuryStatus,
  onRpeChange,
  onInjuryChange,
  t,
}: {
  sessionRpe: number
  injuryStatus: InjuryStatus
  onRpeChange: (value: number) => void
  onInjuryChange: (value: InjuryStatus) => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <>
      <div>
        <p className={KILO.meta}>{t('workout.debrief.sessionFeelTitle')}</p>
        <p className={`mt-1 text-xs ${KILO.text.muted}`}>{t('workout.debrief.sessionFeelHelp')}</p>
        <p className="mt-3 font-mono text-sm font-semibold text-kilo-accent">
          {t('workout.debrief.sessionRpe', { rpe: sessionRpe })}
        </p>
        <input
          type="range"
          min={1}
          max={10}
          value={sessionRpe}
          onChange={(e) => onRpeChange(Number(e.target.value))}
          className={RPE_SLIDER_CLASS}
          aria-label={t('workout.debrief.sessionFeelTitle')}
        />
        <div className="mt-1 flex justify-between px-0.5 text-[10px] text-kilo-muted">
          <span>{t('workout.debrief.rpeEasy')}</span>
          <span>{t('workout.debrief.rpeCurrent', { rpe: sessionRpe })}</span>
          <span>{t('workout.debrief.rpeMax')}</span>
        </div>
      </div>
      <div className="mt-4">
        <p className="text-sm font-semibold text-kilo-text">{t('workout.debrief.injuryPrompt')}</p>
        <p className={`mt-1 text-xs ${KILO.text.muted}`}>{t('workout.debrief.injuryHelp')}</p>
        <div className="mt-3 grid gap-2">
          {INJURY_OPTIONS.map((value) => {
            const selected = injuryStatus === value
            return (
              <button
                key={value}
                type="button"
                className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                  selected
                    ? 'border-kilo-accent bg-kilo-accent/10'
                    : 'border-kilo-border bg-kilo-card hover:border-kilo-accent/30'
                }`}
                onClick={() => onInjuryChange(value)}
              >
                <p className={`text-sm font-semibold ${selected ? 'text-kilo-text' : KILO.text.soft}`}>
                  {t(`workout.debrief.injury.${value}`)}
                </p>
                <p className={`mt-0.5 text-xs ${KILO.text.muted}`}>
                  {t(`workout.debrief.injuryDesc.${value}`)}
                </p>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}

const TIER_STYLE: Record<SessionDebrief['performance_tier'], { className: string }> = {
  excellent: { className: 'text-kilo-accent' },
  solid: { className: 'text-sky-300' },
  partial: { className: 'text-amber-400' },
  minimal: { className: 'text-kilo-muted' },
}

const PostWorkoutSummary = ({
  userId,
  scheduleId,
  setProgress,
  block,
  training,
  dayNumber,
  hasWearableConnected,
  calendarSyncEnabled,
  dietaryLabel,
  meals,
  grocery,
  onDismiss,
  onOpenShopping,
  onOpenMeals,
  onConnectCalendar,
}: PostWorkoutSummaryProps) => {
  const { t } = useTranslation()
  const [sessionRpe, setSessionRpe] = useState(7)
  const [injuryStatus, setInjuryStatus] = useState<InjuryStatus>('NONE')
  const [debrief, setDebrief] = useState<SessionDebrief | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [telemetrySaved, setTelemetrySaved] = useState(false)
  const [feelPanelOpen, setFeelPanelOpen] = useState(false)
  const debriefRef = useRef<HTMLDivElement>(null)

  const upcomingWeek = useMemo(() => upcomingTrainingBlocks(training, 7), [training])

  const deduplicatedSessions = useMemo(() => {
    const seen = new Set<string>()
    return upcomingWeek.filter((session) => {
      const key = `${session.scheduled_date ?? ''}_${session.start_time ?? ''}_${session.block_title ?? ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [upcomingWeek])

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), [])

  const visibleActions = useMemo(
    () => (debrief?.actions ?? []).filter((action) => action.id !== 'connect_wearable'),
    [debrief],
  )

  const initialLoadDone = useRef(false)

  const loadDebrief = useCallback(async (options?: { forceTelemetry?: boolean; forceRefresh?: boolean }) => {
    const forceTelemetry = options?.forceTelemetry ?? false
    const forceRefresh = options?.forceRefresh ?? false
    setLoading(true)
    setError('')

    if (userId && (!telemetrySaved || forceTelemetry)) {
      const { error: feedbackError } = await saveWorkoutFeedback(userId, scheduleId, {
        sessionRpe,
        injuryStatus,
      })
      if (feedbackError) {
        setError(feedbackError)
        setLoading(false)
        return
      }
      setTelemetrySaved(true)
    }

    const { debrief: loaded, error: debriefError } = await fetchSessionDebrief({
      schedule_id: scheduleId,
      set_progress_pct: setProgress,
      session_rpe: sessionRpe,
      injury_status: injuryStatus,
      force_refresh: forceRefresh,
      local_context: userId
        ? {
            userId,
            block,
            dayNumber,
            hasWearableConnected,
            calendarSyncEnabled,
            dietaryLabel,
            meals,
            grocery,
          }
        : undefined,
    })

    setLoading(false)
    if (debriefError) {
      setError(debriefError)
      return
    }
    setDebrief(loaded)
    if (!forceRefresh) {
      setFeelPanelOpen(false)
    }
    if (forceRefresh) {
      requestAnimationFrame(() => {
        debriefRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [userId, telemetrySaved, scheduleId, sessionRpe, injuryStatus, setProgress, block, dayNumber, hasWearableConnected, calendarSyncEnabled, dietaryLabel, meals, grocery])

  useEffect(() => {
    if (initialLoadDone.current) return
    initialLoadDone.current = true
    void loadDebrief()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId])

  const handleAction = (actionId: DebriefActionId) => {
    switch (actionId) {
      case 'open_meals':
        onOpenMeals?.()
        return
      case 'open_shopping':
        onOpenShopping?.()
        return
      case 'sync_calendar':
        onConnectCalendar?.()
        return
      case 'connect_wearable':
        onDismiss()
        return
      case 'rest':
        onDismiss()
        return
      default:
        return
    }
  }

  const tierStyle = debrief ? TIER_STYLE[debrief.performance_tier] : null
  const safeCoachMessage = debrief ? sanitizeCoachText(debrief.coach_message, 'session_debrief') : null
  const safeRecoveryHint = debrief?.recovery_hint
    ? sanitizeCoachText(debrief.recovery_hint, 'default')
    : null

  return (
    <div className={`flex flex-col gap-3 pb-24 ${KILO.bg.page}`}>
      <header>
        <p className={KILO.meta}>{t('workout.debrief.meta')}</p>
        <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{debrief?.headline ?? t('workout.debrief.reading')}</h1>
        <p className={`mt-1 text-sm ${KILO.text.soft}`}>
          {t('workout.debrief.dayProgress', { day: dayNumber, pct: setProgress })}
        </p>
      </header>

      {!debrief && !loading ? (
        <BentoCard gradient="slate">
          <p className={`text-sm ${KILO.text.muted}`}>{t('workout.debrief.prompt')}</p>
          <div className="mt-4">
            <InjuryFeelControls
              sessionRpe={sessionRpe}
              injuryStatus={injuryStatus}
              onRpeChange={setSessionRpe}
              onInjuryChange={setInjuryStatus}
              t={t}
            />
          </div>
          <button
            type="button"
            disabled={loading}
            className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
            onClick={() => void loadDebrief()}
          >
            {t('workout.debrief.generate')}
          </button>
        </BentoCard>
      ) : null}

      {loading ? (
        <BentoCard gradient="forest">
          <p className={`text-sm ${KILO.text.muted}`}>{t('workout.debrief.loading')}</p>
        </BentoCard>
      ) : null}

      {error ? <p className="text-sm text-red-400">{error}</p> : null}

      {debrief ? (
        <>
          <div ref={debriefRef}>
          <BentoCard gradient="forest" featured>
            <div className="flex flex-wrap items-center gap-2">
              {tierStyle ? (
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tierStyle.className}`}>
                  {t(`workout.debrief.tier.${debrief.performance_tier}`)}
                </span>
              ) : null}
              <StatusPill label="Coach Kilo" active />
            </div>
            <p className={`mt-4 text-sm leading-relaxed ${KILO.text.soft}`}>{safeCoachMessage}</p>

            <CollapsedDisclaimer />

            {safeRecoveryHint ? (
              <p className="mt-4 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs leading-relaxed text-sky-950 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-200">
                <MaterialIcon name="monitor_heart" size="sm" className="mt-0.5 shrink-0 text-sky-700 dark:text-sky-300" />
                {safeRecoveryHint}
              </p>
            ) : null}

            {visibleActions.length > 0 ? (
              <div className="mt-4 grid gap-2">
                {visibleActions.map((action) => {
                  const isMeals = action.id === 'open_meals'
                  const isShopping = action.id === 'open_shopping'

                  if (isMeals) {
                    return (
                      <button
                        key={action.id}
                        type="button"
                        className="mb-1 w-full rounded-2xl bg-kilo-accent py-4 text-sm font-semibold text-black"
                        onClick={() => handleAction(action.id)}
                      >
                        {t(`workout.debrief.actions.${action.id}`, { defaultValue: action.label })}
                      </button>
                    )
                  }

                  if (isShopping) {
                    return (
                      <button
                        key={action.id}
                        type="button"
                        className="mb-2 flex w-full items-center justify-center gap-2 rounded-xl border border-neutral-800 bg-neutral-900 py-3 text-sm text-neutral-300"
                        onClick={() => handleAction(action.id)}
                      >
                        <span aria-hidden>🛒</span>
                        {t(`workout.debrief.actions.${action.id}`, { defaultValue: action.label })}
                      </button>
                    )
                  }

                  return (
                    <button
                      key={action.id}
                      type="button"
                      className="group flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-text"
                      onClick={() => handleAction(action.id)}
                    >
                      <MaterialIcon
                        name={
                          action.id === 'sync_calendar'
                            ? 'calendar_month'
                            : 'nightlight'
                        }
                        hoverable
                        size="sm"
                      />
                      {t(`workout.debrief.actions.${action.id}`, { defaultValue: action.label })}
                    </button>
                  )
                })}
              </div>
            ) : null}
          </BentoCard>
          </div>

          <BentoCard gradient="slate">
            {telemetrySaved && !feelPanelOpen ? (
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() => setFeelPanelOpen(true)}
              >
                <div>
                  <p className="text-sm font-semibold text-kilo-text">{t('workout.debrief.adjustFeelTitle')}</p>
                  <p className={`mt-1 text-xs ${KILO.text.muted}`}>
                    {t('workout.debrief.feelRecorded', { rpe: sessionRpe })}
                  </p>
                </div>
                <MaterialIcon name="chevron_right" hoverable size="sm" />
              </button>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-kilo-text">{t('workout.debrief.adjustFeelTitle')}</p>
                  {telemetrySaved ? (
                    <button
                      type="button"
                      className="text-xs font-semibold text-kilo-muted"
                      onClick={() => setFeelPanelOpen(false)}
                    >
                      {t('common.close')}
                    </button>
                  ) : null}
                </div>
                <div className="mt-3">
                  <InjuryFeelControls
                    sessionRpe={sessionRpe}
                    injuryStatus={injuryStatus}
                    onRpeChange={setSessionRpe}
                    onInjuryChange={setInjuryStatus}
                    t={t}
                  />
                </div>
                <button
                  type="button"
                  className="mt-4 flex h-10 w-full items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black"
                  onClick={() => void loadDebrief({ forceTelemetry: true, forceRefresh: true })}
                >
                  {t('workout.debrief.refresh')}
                </button>
              </>
            )}
          </BentoCard>
        </>
      ) : null}

      <BentoCard gradient="slate">
        <div className="flex items-center gap-2">
          <MaterialIcon name="calendar_month" hoverable size="sm" />
          <p className={KILO.meta}>{t('workout.debrief.upNext')}</p>
        </div>
        <div className="mt-4 space-y-3">
          {deduplicatedSessions.length === 0 ? (
            <p className={`text-sm ${KILO.text.muted}`}>{t('workout.debrief.noUpcoming')}</p>
          ) : (
            deduplicatedSessions.slice(0, 4).map((entry) => {
              const isToday = entry.scheduled_date === todayIso
              return (
                <div
                  key={`${entry.id}-${entry.scheduled_date}`}
                  className={`flex items-start gap-3 rounded-xl border p-3 ${
                    isToday ? 'border-kilo-accent/30 bg-kilo-accent/5' : 'border-kilo-border bg-kilo-card/60'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-kilo-soft">
                      {getDayLabel(entry.day_name_id)} · {entry.start_time}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-kilo-text">{entry.block_title}</p>
                  </div>
                  {isToday ? (
                    <span className="shrink-0 rounded-full bg-kilo-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-kilo-accent">
                      {t('workout.debrief.today')}
                    </span>
                  ) : null}
                </div>
              )
            })
          )}
        </div>
      </BentoCard>

      {!calendarSyncEnabled && debrief?.actions.every((action) => action.id !== 'sync_calendar') ? (
        <BentoCard gradient="none" className="mt-4 border border-kilo-border">
          <p className={`text-sm ${KILO.text.soft}`}>
            {t('workout.debrief.calendarHint')}
          </p>
        </BentoCard>
      ) : null}

      <button
        type="button"
        className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-kilo-border bg-kilo-alt text-sm font-semibold text-kilo-text"
        onClick={onDismiss}
      >
        <MaterialIcon name="done_outline" hoverable size="sm" />
        {t('workout.debrief.backToCoach')}
      </button>
    </div>
  )
}

export default PostWorkoutSummary
